// src/main/shell/symbolGrid/chartUtils.ts
// Chart behaviour for the SymbolGrid: the user's theme preference, padding, and
// the chartable check. The PALETTE itself is base-ui's — see chart-themes.ts
// there — because it is a design asset shared by every app, not grid logic.
// Re-exported here so call sites keep one import.

import {
  CHART_THEMES,
  CHART_THEME_NAMES,
  SELECTABLE_CHART_THEMES,
  type ChartThemeName,
  type SelectableChartTheme,
} from '@vitesse/base-ui';

export {
  CHART_THEMES,
  CHART_THEME_NAMES,
  SELECTABLE_CHART_THEMES,
  type ChartThemeName,
  type SelectableChartTheme,
};

/** The theme one step after `theme` in the selectable rotation. Priority-polled
 *  symbols chart in this so they're always a colour distinct from the global theme.
 *  Accepts any string; unknown/legacy values (e.g. 'blue') fold to frost's slot. */
export function nextChartTheme(theme: string): SelectableChartTheme {
  const i = SELECTABLE_CHART_THEMES.indexOf(theme as SelectableChartTheme);
  const base = i === -1 ? 0 : i;
  return SELECTABLE_CHART_THEMES[(base + 1) % SELECTABLE_CHART_THEMES.length];
}

// Global chart-theme preference (applies to every sparkline). Same reactive
// init/get/set/onChange shape as core/theme.ts — getControllerChartConfig reads
// getChartTheme(), the grid's render loop picks it up on the next frame, and
// onChartThemeChange lets the live charts repaint immediately on a switch.
const CHART_THEME_KEY = 'vg-chart-theme';
const DEFAULT_CHART_THEME: ChartThemeName = 'frost';
const chartThemeListeners = new Set<(t: ChartThemeName) => void>();

function resolveChartTheme(): ChartThemeName {
  let v: string | null = null;
  try {
    v = localStorage.getItem(CHART_THEME_KEY);
  } catch {
    /* private mode / storage disabled — use the default */
  }
  return v && (CHART_THEME_NAMES as string[]).includes(v) ? (v as ChartThemeName) : DEFAULT_CHART_THEME;
}

// Memoized: the grid's render loop calls getChartTheme() (via getChartConfig)
// once per frame AND once per changed row on scroll, so an uncached version hits
// localStorage.getItem() dozens-to-hundreds of times/sec — a synchronous
// main-thread read on the paint path, for a value that only changes when the user
// opens the picker. Cache it; setChartTheme (the only same-tab writer) refreshes
// it, and the cross-tab `storage` handler below mirrors what the old per-frame
// read did for another tab's change (next-frame pickup), so behavior is identical.
let cachedChartTheme: ChartThemeName | null = null;

export function getChartTheme(): ChartThemeName {
  if (cachedChartTheme === null) cachedChartTheme = resolveChartTheme();
  return cachedChartTheme;
}
export function setChartTheme(theme: ChartThemeName): void {
  cachedChartTheme = theme;
  try {
    localStorage.setItem(CHART_THEME_KEY, theme);
  } catch {
    /* private mode / storage disabled — the cache above still applies it this page */
  }
  for (const cb of chartThemeListeners) cb(theme);
}
// Cross-tab: another tab's setChartTheme writes localStorage but never runs THIS
// tab's setChartTheme. The old uncached getChartTheme picked that up on the next
// frame; mirror it exactly by refreshing the cache on the storage event (no
// listener fan-out — the grid's per-frame read repaints, as before).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === CHART_THEME_KEY) cachedChartTheme = resolveChartTheme();
  });
}
export function onChartThemeChange(cb: (t: ChartThemeName) => void): () => void {
  chartThemeListeners.add(cb);
  return () => chartThemeListeners.delete(cb);
}

/**
 * Padding factor by sample rate — lower rates need more left-edge padding for
 * smooth scrolling.
 */
export function calculatePaddingFactor(sampleRateHz: number): number {
  if (sampleRateHz <= 1) return 0.15;
  if (sampleRateHz <= 5) return 0.1;
  if (sampleRateHz <= 10) return 0.05;
  return 0.02;
}

/** Is a value numeric and chartable? */
export function isChartableValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const num = Number(value);
  return !isNaN(num) && isFinite(num);
}
